// Aba "Fluxos" do Flowbuilder (/flowbuilders): abas + busca + "Novo fluxo",
// grade de cards 2 colunas (mockup "Fluxos A"), modais Novo fluxo
// (templates), Duplicar e Excluir (AerosConfirmModal com contagem de frases
// gatilho que usam o fluxo).
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputBase from "@material-ui/core/InputBase";
import CircularProgress from "@material-ui/core/CircularProgress";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import { AccountTree, ContentCopy, Edit, DeleteOutline, PlayArrow, Pause } from "@mui/icons-material";

import api from "../../services/api";
import MainContainer from "../../components/MainContainer";
import FlowBuilderNav from "../../components/FlowBuilderNav";
import FlowBuilderModal from "../../components/FlowBuilderModal";
import AerosConfirmModal from "../../components/AerosConfirmModal";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AEROS } from "../../styles/aerosTokens";
import useFlowPageStyles from "./flowPageStyles";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    grid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 14,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr", gap: 12 },
    },
    card: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.07)"}`,
      borderRadius: 14,
      padding: 20,
      display: "flex",
      gap: 16,
      alignItems: "flex-start",
      transition: "border-color 0.15s ease, transform 0.15s ease",
      cursor: "pointer",
      "&:hover": { transform: "translateY(-2px)", borderColor: alpha(theme.palette.primary.main, 0.4) },
    },
    icon: {
      width: 46,
      height: 46,
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      "& .MuiSvgIcon-root": { fontSize: 23 },
    },
    iconActive: {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
      color: isLight ? AEROS.stateTextOnLight.purple : "#C08BFF",
    },
    iconInactive: {
      backgroundColor: isLight ? "rgba(15,20,40,0.05)" : "rgba(255,255,255,0.05)",
      color: theme.palette.aeros.textTertiary,
    },
    name: { fontSize: 15, fontWeight: 600, letterSpacing: "-0.01em", color: theme.palette.text.primary, lineHeight: 1.4 },
    footer: { display: "flex", alignItems: "center", gap: 8, marginTop: 14 },
    steps: { fontSize: 11, color: theme.palette.aeros.textTertiary },
    actions: { marginLeft: "auto", display: "flex", gap: 6 },
  };
});

const countSteps = (flow) => (Array.isArray(flow?.flow?.nodes) ? flow.flow.nodes.length : null);

const FlowBuilder = () => {
  const page = useFlowPageStyles();
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [flows, setFlows] = useState([]);
  const [phraseUsage, setPhraseUsage] = useState({}); // flowId → nº de frases gatilho
  const [modal, setModal] = useState(null); // { id, name } | {} novo
  const [duplicating, setDuplicating] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, camp] = await Promise.all([
        api.get("/flowbuilder"),
        api.get("/flowcampaign").catch(() => ({ data: { flow: [] } })),
      ]);
      setFlows(data.flows || []);
      const usage = {};
      (camp.data?.flow || []).forEach((c) => {
        usage[c.flowId] = (usage[c.flowId] || 0) + 1;
      });
      setPhraseUsage(usage);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(
    () => flows.filter((f) => f.name.toLowerCase().includes(search.toLowerCase())),
    [flows, search]
  );

  const handleDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/flowbuilder/${deleting.id}`);
      toast.success(i18n.t("aeros.flow.list.deleted"));
      setDeleting(null);
      load();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const handleDuplicate = async () => {
    setBusy(true);
    try {
      await api.post("/flowbuilder/duplicate", { flowId: duplicating.id });
      toast.success(i18n.t("aeros.flow.list.duplicated"));
      setDuplicating(null);
      load();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const usedBy = deleting ? phraseUsage[deleting.id] || 0 : 0;

  return (
    <MainContainer>
      <FlowBuilderModal
        open={!!modal}
        onClose={() => setModal(null)}
        flowId={modal?.id}
        nameWebhook={modal?.name}
        onSave={load}
      />
      <AerosConfirmModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        icon={<DeleteOutline />}
        title={i18n.t("aeros.flow.delete.title", { name: deleting?.name || "" })}
        description={`${usedBy ? `${i18n.t("aeros.flow.delete.usedBy", { count: usedBy })} ` : ""}${i18n.t("aeros.flow.delete.irreversible")}`}
        confirmLabel={i18n.t("aeros.flow.delete.confirm")}
        cancelLabel={i18n.t("aeros.flow.cancel")}
        loading={busy}
      />
      <AerosConfirmModal
        open={!!duplicating}
        onClose={() => setDuplicating(null)}
        onConfirm={handleDuplicate}
        icon={<ContentCopy />}
        tone="info"
        primary
        title={i18n.t("aeros.flow.list.duplicateTitle", { name: duplicating?.name || "" })}
        description={i18n.t("aeros.flow.list.duplicateText")}
        confirmLabel={i18n.t("aeros.flow.list.duplicateConfirm")}
        cancelLabel={i18n.t("aeros.flow.cancel")}
        loading={busy}
      />

      <div className={page.pageHeader}>
        <FlowBuilderNav counts={{ flows: flows.length }} />
        <div className={page.headerActions}>
          <InputBase
            className={page.searchPill}
            placeholder={i18n.t("aeros.flow.list.search")}
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            startAdornment={<SearchIcon />}
            inputProps={{ "aria-label": i18n.t("aeros.flow.list.search") }}
          />
          <Button variant="contained" color="primary" className={page.gradientButton} startIcon={<AddIcon />} onClick={() => setModal({})}>
            {i18n.t("aeros.flow.list.new")}
          </Button>
        </div>
      </div>

      <div className={page.pageBody}>
        {loading && !flows.length ? (
          <div className={page.loading}><CircularProgress color="primary" /></div>
        ) : visible.length === 0 ? (
          <div className={page.emptyState}>
            <AccountTree className={page.emptyIcon} />
            <div className={page.emptyTitle}>{i18n.t(search ? "aeros.flow.list.emptySearch" : "aeros.flow.list.empty")}</div>
            <div className={page.emptyDescription}>{i18n.t(search ? "aeros.flow.list.emptySearchHint" : "aeros.flow.list.emptyHint")}</div>
            {!search && (
              <Button variant="contained" color="primary" className={page.gradientButton} startIcon={<AddIcon />} style={{ marginTop: 24 }} onClick={() => setModal({})}>
                {i18n.t("aeros.flow.list.createFirst")}
              </Button>
            )}
          </div>
        ) : (
          <div className={classes.grid}>
            {visible.map((flow) => {
              const steps = countSteps(flow);
              return (
                <div
                  key={flow.id}
                  className={classes.card}
                  role="button"
                  tabIndex={0}
                  onClick={(e) => !e.target.closest("button") && history.push(`/flowbuilder/${flow.id}`)}
                  onKeyDown={(e) => e.key === "Enter" && history.push(`/flowbuilder/${flow.id}`)}
                >
                  <div className={`${classes.icon} ${flow.active ? classes.iconActive : classes.iconInactive}`}>
                    <AccountTree />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className={classes.name}>{flow.name}</div>
                    <div className={classes.footer}>
                      <span className={`${page.statusPill} ${flow.active ? page.pillActive : page.pillInactive}`}>
                        {flow.active ? <PlayArrow /> : <Pause />}
                        {i18n.t(flow.active ? "aeros.flow.list.active" : "aeros.flow.list.inactive")}
                      </span>
                      {steps !== null && <span className={classes.steps}>{i18n.t("aeros.flow.list.steps", { count: steps })}</span>}
                      <div className={classes.actions}>
                        <IconButton className={page.actionButton} aria-label={i18n.t("aeros.flow.list.editAria")} onClick={() => setModal({ id: flow.id, name: flow.name })}>
                          <Edit />
                        </IconButton>
                        <IconButton className={page.actionButton} aria-label={i18n.t("aeros.flow.list.duplicateAria")} onClick={() => setDuplicating(flow)}>
                          <ContentCopy />
                        </IconButton>
                        <IconButton className={`${page.actionButton} ${page.deleteButton}`} aria-label={i18n.t("aeros.flow.list.deleteAria")} onClick={() => setDeleting(flow)}>
                          <DeleteOutline />
                        </IconButton>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </MainContainer>
  );
};

export default FlowBuilder;
